<script setup lang="ts">
/**
 * UsersTable — IAM-specific DataTable wrapper.
 *
 * Most of the column logic lives in `users-table-columns.ts` because
 * <script setup> doesn't support named exports and the column-menu
 * also needs the column metadata. This component is a thin shell:
 * compute the visible subset, hand it to DataTable.
 *
 * Server-side pagination isn't used here — the IAM list endpoint
 * returns an unpaginated array. CMS admins are typically <100 users
 * so the table renders all rows at once. If the row count grows,
 * server-side pagination will need to land on the backend first.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import DataTable from '@/components/ui/DataTable.vue'
import { buildColumns } from '../users-table-columns'
import type { UserView } from '../api'

interface Props {
  data: UserView[]
  visible: string[]
  loading?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits<{
  edit: [user: UserView]
  permissions: [user: UserView]
  resetPassword: [user: UserView]
  delete: [user: UserView]
}>()

const i18n = useI18n()

/**
 * Build the full column list once per i18n instance, then filter
 * by visibility. We rebuild columns inside a computed so that locale
 * changes (Sprint 2 brings Arabic) re-render the headers.
 */
const allColumns = computed(() =>
  buildColumns(i18n, {
    onEdit: (u) => emit('edit', u),
    onPermissions: (u) => emit('permissions', u),
    onResetPassword: (u) => emit('resetPassword', u),
    onDelete: (u) => emit('delete', u),
  }),
)

const visibleColumns = computed(() =>
  allColumns.value.filter((c) =>
    props.visible.includes(c.id as string),
  ),
)
</script>

<template>
  <DataTable
    :data="data"
    :columns="visibleColumns"
    :total="data.length"
    :page="0"
    :size="data.length || 1"
    :loading="loading"
    hide-pagination
    :empty-title="$t('iam.table.empty.title')"
    :empty-description="$t('iam.table.empty.description')"
  />
</template>
